import { normalizePath, useSetFlowTo } from 'Simple/Flow.js'
import { useDataValue } from 'Simple/Data.js'
import { useMutation } from 'Data/Api.js'
import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications.js'
import mutationAppsConnectionResourceEnable from './mutation-apps-connection-resource-enable.graphql.js'
import mutationAppsConnectionResourceDisable from './mutation-apps-connection-resource-disable.graphql.js'
import mutationApiAuthorize from './mutation-api-authorize.graphql.js'
import mutationConnectApiToken from './mutation-connect-api-token.graphql.js'
import mutationConnectOAuth2ClientCredentials from './mutation-connect-oauth2-client-credentials.graphql.js'

import { useDataSubmit } from 'Simple/Data.js'
import { useRef } from 'react'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let [, executeAppsConnectionResourceEnableMutation] = useMutation(
    mutationAppsConnectionResourceEnable
  )
  let [, executeAppsConnectionResourceDisableMutation] = useMutation(
    mutationAppsConnectionResourceDisable
  )
  let [, executeApiAuthorizeMutation] = useMutation(mutationApiAuthorize)
  let [, executeConnectApiTokenMutation] = useMutation(mutationConnectApiToken)
  let [, executeConnectOAuth2ClientCredentialsMutation] = useMutation(
    mutationConnectOAuth2ClientCredentials
  )

  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let connection_title = useDataValue({
    context: 'app',
    path: 'name',
    viewPath: props.viewPath,
  })

  let location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location._id',
  })

  let submitConnections = useDataSubmit({
    context: 'app',
    path: 'connections',
    viewPath: props.viewPath,
  })
  let authWindow = useRef(null)

  return async function onSubmit({ value, originalValue, args, change }) {
    switch (args.type) {
      case 'activate_location_connection': {
        let mutationResponse =
          await executeAppsConnectionResourceEnableMutation({
            connection_id: args.connection_id,
            resource_id: location_id,
          })

        if (
          mutationResponse.error ||
          !mutationResponse.data.apps_connection_resource_enable.ok
        ) {
          return notify(
            notifyError(
              'An error occurred trying to enable the connection. Please refresh the page and try again.'
            )
          )
        } else {
          notify(notifySuccess(`Connection to ${connection_title} is enabled.`))
        }
        submitConnections({ type: 'refetch' })

        return
      }

      case 'complete_setup': {
        setFlowTo(
          normalizePath(
            props.viewPath,
            'ToggleLocationConnection/SetupAutomations/Content'
          )
        )
        return
      }

      case 'disable_connection': {
        let mutationResponse =
          await executeAppsConnectionResourceDisableMutation({
            id: args.connection_resource_id,
          })

        if (mutationResponse.error) {
          return notify(
            notifyError(
              'An error occurred trying to disable the connection. Please refresh the page and try again.'
            )
          )
        } else {
          notify(
            notifySuccess(`Connection to ${connection_title} is disabled.`)
          )
          submitConnections({ type: 'refetch' })
        }
        return
      }

      case 'connect': {
        setFlowTo(normalizePath(props.viewPath, 'Actions/Reconnect/Content'))

        let mutationResponse = await executeApiAuthorizeMutation({
          app_id: value.app_id,
          connection_id: value?.id,
          location_id,
          // TO DO: take this from dynamically generated connection configuration inputs
          // see: https://www.notion.so/getgreyfinch/App-Connections-Refactor-Proposal-96b3ef598e3e4db9b6880b42da3aae09?pvs=4
          connection_properties: {},
        })

        if (
          mutationResponse.error ||
          !mutationResponse.data.apps_external_api_authorize.id ||
          !mutationResponse.data.apps_external_api_authorize.url
        ) {
          return notify(
            notifyError(
              'An error occurred trying to connect the app. Please refresh the page and try again.'
            )
          )
        }

        if (mutationResponse.data.apps_external_api_authorize.url) {
          authWindow.current = window.open(
            mutationResponse.data.apps_external_api_authorize.url,
            'popup',
            `height=768,width=1024,top=${(window.innerHeight - 768) / 2},left=${
              (window.innerWidth - 1024) / 2
            }`
          )
        }

        return
      }

      case 'cancel': {
        try {
          authWindow.current.close()
        } catch (_) {}

        return
      }
      case 'active': {
        submitConnections({ type: 'refetch' })

        setFlowTo(normalizePath(props.viewPath, 'Actions/Reconnect/No'))

        return notify(
          notifySuccess('The application was connected successfully')
        )
      }

      case 'connect-api-token': {
        // Token is optional - can be provided by user or pre-configured in app definition
        if (!value.token && !value.app?.connection?.token) {
          return notify(notifyError('Token is required to reconnect this app'))
        }

        let mutationResponse = await executeConnectApiTokenMutation({
          app_id: value.app_id,
          connection_id: value.id,
          location_id,
          connection_properties: {},
          token: value.token ? value.token.trim() : null,
          expires_at: value.tokenExpiresAt || null,
        })

        if (
          mutationResponse.error ||
          !mutationResponse.data.apps_connect_api_token.connection_id
        ) {
          return notify(
            notifyError(
              'An error occurred trying to reconnect the app. Please refresh the page and try again.'
            )
          )
        }

        submitConnections({ type: 'refetch' })
        setFlowTo(normalizePath(props.viewPath, 'Actions/Reconnect/No'))

        return notify(
          notifySuccess('The application was reconnected successfully')
        )
      }

      case 'connect-oauth2-authorization-code': {
        setFlowTo(normalizePath(props.viewPath, 'Actions/Reconnect/OAuth2'))

        let mutationResponse = await executeApiAuthorizeMutation({
          app_id: value.app_id,
          connection_id: value.id,
          location_id,
          connection_properties: {},
        })

        if (
          mutationResponse.error ||
          !mutationResponse.data.apps_external_api_authorize.id ||
          !mutationResponse.data.apps_external_api_authorize.url
        ) {
          return notify(
            notifyError(
              'An error occurred trying to reconnect the app. Please refresh the page and try again.'
            )
          )
        }

        if (mutationResponse.data.apps_external_api_authorize.url) {
          authWindow.current = window.open(
            mutationResponse.data.apps_external_api_authorize.url,
            'popup',
            `height=768,width=1024,top=${(window.innerHeight - 768) / 2},left=${
              (window.innerWidth - 1024) / 2
            }`
          )

          change(next => {
            next.connecting = true
          })
        }

        return
      }

      case 'connect-oauth2-client-credentials': {
        let mutationResponse =
          await executeConnectOAuth2ClientCredentialsMutation({
            app_id: value.app_id,
            connection_id: value.id,
            location_id,
            connection_properties: {},
          })

        if (
          mutationResponse.error ||
          !mutationResponse.data.apps_connect_oauth2_client_credentials
            .connection_id
        ) {
          return notify(
            notifyError(
              'An error occurred trying to reconnect the app. Please refresh the page and try again.'
            )
          )
        }

        submitConnections({ type: 'refetch' })

        return notify(
          notifySuccess('The application was reconnected successfully')
        )
      }

      case 'cancel-oauth2-authorization-code-connection': {
        try {
          authWindow.current.close()
        } catch (_) {}

        change(next => {
          next.connecting = false
        })

        return
      }

      case 'inactive':
      case 'error':
      case 'error_refresh':
      case 'expired': {
        // Perform no action for these statuses
        return
      }

      default: {
        submitConnections({ type: 'refetch' })

        return
      }
    }
  }
}
